import type { IconName } from "../components/icons";
import type { SpendByKind, SpendDay, SpendKind, SpendWorkload } from "./types";

/**
 * The three things credit is spent on, as the billing page presents them.
 *
 * The page used to present none of them. It listed workloads in one flat run —
 * an app, a database and the organization's whole token spend as peers, ordered
 * by amount — so the question a customer actually arrives with, "how much of
 * this is infrastructure and how much is AI", had to be answered by reading a
 * list and grouping it in your head. Two of the three are metered per hour
 * against a monthly ceiling and the third per call with no ceiling at all,
 * which is a difference the old layout could not express either.
 *
 * The metadata lives here rather than in the component so that the labels, the
 * links and the meter descriptions are asserted in tests, and so the icon and
 * route for each kind are the same ones the nav rail uses. A customer who clicks
 * "Apps" in a breakdown should land where "Apps" in the rail goes.
 */
export interface SpendKindMeta {
  kind: SpendKind;
  icon: IconName;
  /** Where the rail sends this kind. Same target, same symbol. */
  to: string;
  /**
   * Which meter sentence applies, under `billing.meter`.
   *
   * A key rather than the words, because two of the three facts this module
   * holds are copy and it is imported by pages rather than being one — the
   * label reads `billing.kind.<kind>.label` and the count
   * `billing.kind.<kind>.noun`, which is a plural form per language rather
   * than a noun with an "s" glued on.
   */
  meter: "capped" | "perCall" | "purchase";
  /** Whether the kind is a set of workloads that can be counted at all. */
  countable: boolean;
}

export const SPEND_KIND_META: Record<SpendKind, SpendKindMeta> = {
  app: {
    kind: "app",
    icon: "apps",
    to: "/apps",
    meter: "capped",
    countable: true,
  },
  database: {
    kind: "database",
    icon: "database",
    to: "/databases",
    meter: "capped",
    countable: true,
  },
  schedule: {
    kind: "schedule",
    icon: "clock",
    to: "/schedules",
    /*
     * The same words apps and databases carry, because it is the same rule --
     * an hourly rate that stops at a monthly figure. A schedule differs in
     * having one price rather than a tier, which is the breakdown's job to
     * show, not this line's.
     */
    meter: "capped",
    countable: true,
  },
  /*
   * Between schedules and inference, matching SPEND_KINDS on the API. The order
   * is the chart's stack and the legend at once, so the two cannot disagree only
   * if there is one list.
   */
  tunnel: {
    kind: "tunnel",
    icon: "lock",
    to: "/tunnels",
    /* Same rule as every add-on: one flat price, hourly, stopping at the month. */
    meter: "capped",
    countable: true,
  },
  inference: {
    kind: "inference",
    /*
     * "AI inference", matching the nav's "AI Inference" and the API's own row
     * name, rather than "Tokens". The unit is not the thing being bought.
     */
    icon: "models",
    to: "/ai",
    /*
     * "Per call" rather than "Per token", which stopped being true of the whole
     * kind when image and video generation went on sale: those are charged what
     * the request cost to serve, and a customer whose AI spend is entirely video
     * would read a meter describing a unit their bill does not contain. The
     * contrast this line exists to draw is with the two above it — metered while
     * running, stopping at a monthly figure — and "no ceiling" is the half that
     * carries it either way.
     */
    meter: "perCall",
    /*
     * False on purpose. Inference is one org-wide roll-up rather than a set of
     * workloads, so a count would read "1 model" or "1 inference" — both false.
     * The AI page is where per-model detail lives.
     */
    countable: false,
  },
  /*
   * Last, after inference, matching SPEND_KINDS on the API. A domain is the
   * one kind that is neither metered nor per call: it is bought, once, for a
   * year or more, and renewed the same way. The meter line says so.
   */
  domain: {
    kind: "domain",
    icon: "globe",
    to: "/domains",
    meter: "purchase",
    countable: true,
  },
};

export interface SpendKindRow extends SpendKindMeta {
  spentMicros: number;
  workloads: number;
  /** Fraction of the month's total, 0–1. Zero when nothing has been spent. */
  share: number;
}

/**
 * The breakdown, ready to render.
 *
 * Every kind is kept even at zero. A card that appears the first time a customer
 * calls a model, shifting the two beside it, is a layout that moves for a reason
 * the reader cannot see — and a zero here is information: it says this platform
 * does that too, and you are not paying for it.
 */
export function spendKindRows(byKind: SpendByKind[], totalMicros: number): SpendKindRow[] {
  return byKind.map((entry) => ({
    ...SPEND_KIND_META[entry.kind],
    spentMicros: entry.spentMicros,
    workloads: entry.workloads,
    share: totalMicros > 0 ? entry.spentMicros / totalMicros : 0,
  }));
}

/** "3 apps", "1 database", and nothing at all for inference. */
/** A countable kind is any but inference, which is one org-wide roll-up. */
export type CountableKind = Exclude<SpendKind, "inference">;

export function spendKindCount(row: SpendKindRow): { kind: CountableKind; count: number } | null {
  return row.countable ? { kind: row.kind as CountableKind, count: row.workloads } : null;
}

/**
 * A day's spend, named by kind for the chart's readout.
 *
 * Zero parts are dropped: "databases $0.000000" in a hover readout is a clause
 * that says nothing, and on a day with one kind the readout should be the total
 * alone rather than the total plus one restatement of it.
 */
export function spendDayParts(day: SpendDay): { kind: SpendKind; value: number }[] {
  return [
    { kind: "app" as const, value: day.appMicros },
    { kind: "database" as const, value: day.databaseMicros },
    { kind: "schedule" as const, value: day.scheduleMicros },
    { kind: "tunnel" as const, value: day.tunnelMicros },
    { kind: "inference" as const, value: day.inferenceMicros },
    { kind: "domain" as const, value: day.domainMicros ?? 0 },
  ].filter((part) => part.value > 0);
}

/**
 * The month's workloads, grouped under their kind and ordered by spend.
 *
 * Grouped because the flat list buried the thing being looked for. Ten apps
 * ordered by amount put the one inference row wherever its total happened to
 * land, so a customer scanning for their AI spend had to read every line to find
 * out it was ninth. Empty groups are dropped here, unlike in the breakdown above:
 * a heading over no rows is furniture, where a card reading zero is a fact.
 */
export function groupWorkloadsByKind(
  workloads: SpendWorkload[],
): { meta: SpendKindMeta; workloads: SpendWorkload[]; spentMicros: number }[] {
  return (Object.keys(SPEND_KIND_META) as SpendKind[])
    .map((kind) => {
      const rows = workloads
        .filter((workload) => workload.kind === kind)
        .sort((a, b) => b.spentMicros - a.spentMicros);

      return {
        meta: SPEND_KIND_META[kind],
        workloads: rows,
        spentMicros: rows.reduce((sum, row) => sum + row.spentMicros, 0),
      };
    })
    .filter((group) => group.workloads.length > 0);
}
